import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AuthAwareNavbar from '../components/AuthAwareNavbar';
import AuthModal from '../components/AuthModal';
import PremiumActivityModalShell from '../components/PremiumActivityModalShell';
import {
  coLearningInkBody,
  coLearningInkMuted,
  coLearningInkTitle,
} from '../ui/coLearningModalTheme';
import createArtImage from '../assets/create-art.png';
import mainImage from '../assets/main.png';
import shareArtImage from '../assets/share-art.png';
import growTogetherImage from '../assets/grow-together.png';
import artmostudioBackground from '../assets/artmostudio.png';
import artmogalleryBackground from '../assets/artmogallery.png';
import '../styles/homePagePremium.css';

const widgets = [
  {
    title: 'Create with Artmosphere',
    shortDescription: "Make today's artwork",
    description:
      'Create Artmosphere Books with storyboarding, character library, and AI-powered scene generation.',
    link: '/create-books',
    image: createArtImage,
    cta: 'Start',
  },
  {
    title: 'Artist Corner',
    shortDescription: 'Explore art styles',
    description:
      "Your child's creative home inside Artmosphere: gallery, Magic Studio, and learning from artists around the world.",
    link: '/gallery/studio',
    image: shareArtImage,
    cta: 'Explore',
  },
  {
    title: 'Artist Salon',
    shortDescription: 'Learn with artist mentors',
    description:
      'Teach art like never before with assignments, co-learning spaces, and progress tracking.',
    link: '/grow',
    image: growTogetherImage,
    cta: 'Enter',
  },
];

const slides = [
  {
    title: 'Your Gallery & Learning Paths',
    bullets: [
      'Keep all your artworks in a private gallery and upload new pieces anytime.',
      'Follow guided paths using your art and work from kids around the world.',
      'Reuse gallery pieces in Magic Studio, stories, and books.',
    ],
    image: shareArtImage,
  },
  {
    title: 'Magic Studio',
    bullets: [
      'Art Appreciation: a kid-friendly look at what makes artwork special.',
      'Reimagine: transform your piece into 50+ art styles.',
      'Ways of Seeing: place artworks in new scenes and contexts.',
    ],
    image: artmostudioBackground,
  },
  {
    title: 'Learning Space',
    bullets: [
      'Book co-learning walls by page and activity.',
      'Learn with examples from artists around the world.',
      'Daily prompts with streaks and badges.',
    ],
    image: artmogalleryBackground,
  },
];

function widgetDisplayTitle(title) {
  if (title === 'Create with Artmosphere') return 'Create';
  if (title === 'Artist Salon') return 'Artist Salon';
  return title;
}

export default function HomePageNew() {
  const navigate = useNavigate();
  const { user, isAuthenticated, membership, refreshMembership } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  const [showAuthOptions, setShowAuthOptions] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [showMentorAccessInfo, setShowMentorAccessInfo] = useState(false);

  const goArtistCorner = () => {
    if (!isAuthenticated) {
      setAuthMode('signup');
      setShowAuthModal(true);
      return;
    }
    if (user?.accountType === 'mentor') {
      if (user?.mentorType === 'organization' && user?.isPrimaryId) {
        navigate('/organization-dashboard');
      } else {
        navigate('/mentor-dashboard');
      }
    } else {
      navigate('/my-art-world');
    }
  };

  const handleWidgetClick = (widget, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (widget.title === 'Create with Artmosphere') {
      navigate('/create-books');
      return;
    }
    if (widget.title === 'Artist Corner') {
      setShowPreviewModal(true);
      setPreviewIndex(0);
      return;
    }
    if (widget.title === 'Artist Salon') {
      setShowMentorAccessInfo(true);
      return;
    }
    navigate(widget.link);
  };

  const handleAuthChoice = (mode) => {
    setAuthMode(mode);
    setShowAuthOptions(false);
    setShowAuthModal(true);
  };

  const handleEnterArtistCorner = async () => {
    try {
      let data = membership;
      if (!data || !data.membership) {
        data = await refreshMembership(user?.uid);
      }
      const allowed = data?.allowed === true;
      const m = data?.membership || {};
      const isActive = m.status === 'active' || m.status === 'trialing';
      const hasHistory = m.status || m.expiresAt || m.planId;
      setShowPreviewModal(false);
      if (allowed || isActive) {
        navigate('/my-art-world');
      } else {
        navigate('/membership', {
          state: { isRenewal: !!hasHistory, userData: data || null },
        });
      }
    } catch {
      setShowPreviewModal(false);
      navigate('/membership', { state: { isRenewal: false } });
    }
  };

  const navHidden = showPreviewModal;

  return (
    <div className="home-premium-page relative">
      <div className="home-premium-spotlight" aria-hidden />
      <div className="home-premium-grain" aria-hidden />

      <div className="home-premium-shell relative z-10">
        {!navHidden && <AuthAwareNavbar homePremium />}

        <main className="home-premium-main home-animate-in">
          {/* Hero — embedded on page (no glass panel) */}
          <section className="home-hero-embedded shrink-0">
            <div className="home-hero-grid grid grid-cols-1 items-center gap-3 sm:grid-cols-[1fr_auto] sm:gap-5">
              <div className="min-w-0 text-center sm:text-left">
                <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[#9A7A55]">
                  Artmosphere
                </p>
                <h1 className="mt-1 font-serif text-2xl font-semibold leading-tight text-[#2F251B] sm:text-3xl md:text-[2rem]">
                  Create. Learn. Share.
                </h1>
                <p className={`mt-1.5 line-clamp-2 text-xs leading-relaxed sm:text-sm ${coLearningInkMuted}`}>
                  Your personal art studio where ideas, colours and stories come alive.
                </p>
                <div className="mt-2.5 flex flex-wrap items-center justify-center gap-2.5 sm:justify-start">
                  <button
                    type="button"
                    onClick={() => navigate('/create-books')}
                    className="home-premium-cta-primary rounded-full px-5 py-2 text-xs font-semibold sm:text-sm"
                  >
                    Start Creating
                  </button>
                  <button
                    type="button"
                    onClick={goArtistCorner}
                    className="home-premium-cta-secondary rounded-full px-4 py-2 text-xs font-semibold sm:text-sm"
                  >
                    Artist Corner
                  </button>
                </div>
              </div>
              <img
                src={mainImage}
                alt=""
                className="home-hero-image mx-auto h-[5.5rem] w-auto max-w-[11rem] rounded-2xl border border-[#E8C97A]/50 object-cover shadow-sm sm:mx-0 md:h-[5rem] md:max-w-[11.5rem]"
              />
            </div>
          </section>

          {/* Three cards — fill viewport, no extra section heading */}
          <section className="home-widget-grid grid w-full grid-cols-1 gap-3 sm:gap-4 lg:min-h-0 lg:flex-1 lg:grid-cols-3 lg:items-start lg:content-start lg:gap-2">
            {widgets.map((widget) => (
              <div
                key={widget.title}
                className="home-journey-card home-widget-card flex flex-col overflow-hidden rounded-[22px] sm:rounded-[26px]"
              >
                <div className="home-gold-rule shrink-0" aria-hidden />
                <div className="home-widget-card-image relative overflow-hidden bg-white">
                  <img
                    src={widget.image}
                    alt=""
                    className="home-journey-img h-full w-full object-cover"
                    style={{ objectPosition: 'top center' }}
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2F251B]/12 via-transparent to-transparent" />
                </div>
                <div className="home-widget-card-body border-t border-[#F5D99B]/35 bg-[#FFFCF7] px-3 pt-2 pb-2 sm:px-3.5 sm:pt-2.5 sm:pb-2">
                  <div className="home-widget-card-text space-y-1">
                    <h3 className={`font-serif text-sm font-semibold leading-tight sm:text-[15px] ${coLearningInkTitle}`}>
                      {widgetDisplayTitle(widget.title)}
                    </h3>
                    <p className="text-[11px] font-medium leading-snug text-[#8B6914] sm:text-xs">
                      {widget.shortDescription}
                    </p>
                    <p className={`line-clamp-2 text-[10px] leading-snug sm:text-[11px] ${coLearningInkMuted}`}>
                      {widget.description}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (widget.title === 'Create with Artmosphere') {
                        navigate('/create-books');
                      } else {
                        handleWidgetClick(widget, e);
                      }
                    }}
                    onTouchStart={(e) => {
                      if (widget.title === 'Create with Artmosphere') {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate('/create-books');
                      }
                    }}
                    className="home-widget-card-cta home-premium-cta-card w-full rounded-xl py-2 text-xs font-semibold touch-manipulation"
                  >
                    {widget.cta}
                  </button>
                </div>
              </div>
            ))}
          </section>

          {/* Community CTA strip — below thumbnails */}
          <section className="home-thumbnail-community-cta shrink-0">
            <div className="home-community-cta-strip">
              <div className="home-gold-rule shrink-0" aria-hidden />
              <p className={`text-center text-xs leading-snug sm:text-sm ${coLearningInkBody}`}>
                {isAuthenticated
                  ? 'Ready to keep creating? Step into your gallery, Magic Studio, and learning paths.'
                  : 'Ready to get started? Create, learn, and share with young artists around the world.'}
              </p>
              <button
                type="button"
                onClick={() => {
                  if (isAuthenticated) {
                    goArtistCorner();
                  } else {
                    setAuthMode('signup');
                    setShowAuthModal(true);
                  }
                }}
                className="home-premium-cta-primary w-full rounded-full px-6 py-2.5 text-sm font-semibold sm:w-auto sm:min-w-[14rem]"
              >
                {isAuthenticated ? 'Visit Artist Corner' : 'Join Community'}
              </button>
            </div>
          </section>
        </main>
      </div>

      {/* Artist Corner preview — original flow */}
      <PremiumActivityModalShell
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title="Artist Corner"
        eyebrow="Explore"
        subtitle="Explore Art Styles"
        maxWidthClass="max-w-5xl"
        zIndexClass="z-[200100]"
        ariaLabelledBy="home-artist-corner-modal-title"
      >
        <div className="flex min-h-0 flex-col">
          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-2">
              <div>
                <h4 className={`mb-3 text-lg font-semibold ${coLearningInkTitle}`}>
                  {slides[previewIndex].title}
                </h4>
                <ul className={`list-disc space-y-2 pl-5 text-sm ${coLearningInkBody}`}>
                  {slides[previewIndex].bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </div>
              <div className="home-museum-frame">
                <div className="home-museum-frame-inner overflow-hidden rounded-xl">
                  <img
                    src={slides[previewIndex].image}
                    alt=""
                    className="h-56 w-full object-cover md:h-64"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-center gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPreviewIndex(i)}
                  className={`h-2.5 w-2.5 rounded-full transition ${i === previewIndex ? 'home-premium-dot-active' : 'home-premium-dot-idle'}`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
          <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[#F5D99B]/50 bg-[#FFFDF9]/98 px-5 py-4 backdrop-blur-sm sm:px-6">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
                disabled={previewIndex === 0}
                className="home-premium-cta-secondary inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <button
                type="button"
                onClick={() => setPreviewIndex((i) => Math.min(slides.length - 1, i + 1))}
                disabled={previewIndex === slides.length - 1}
                className="home-premium-cta-primary inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40"
              >
                Next <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
            {isAuthenticated ? (
              <button
                type="button"
                onClick={handleEnterArtistCorner}
                className="home-premium-cta-primary rounded-full px-6 py-2.5 text-sm font-semibold"
              >
                Enter Now
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
                  className="home-premium-cta-secondary rounded-full px-4 py-2 text-sm font-semibold"
                >
                  Login
                </button>
                <button
                  type="button"
                  onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }}
                  className="home-premium-cta-primary rounded-full px-4 py-2 text-sm font-semibold"
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      </PremiumActivityModalShell>

      {/* Artist Salon — original access info */}
      <PremiumActivityModalShell
        open={showMentorAccessInfo}
        onClose={() => setShowMentorAccessInfo(false)}
        title="Artist Salon"
        eyebrow="Coming soon"
        subtitle="Pilot access"
        maxWidthClass="max-w-md"
        zIndexClass="z-[200100]"
      >
        <div className={`space-y-4 p-6 text-sm ${coLearningInkBody}`}>
          <p>Artist Salon is a special space for art mentors and organizations.</p>
          <p className={coLearningInkMuted}>
            This feature is coming soon as part of our pilot. You can keep exploring Artist Corner and your creative spaces for now.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setShowMentorAccessInfo(false)}
              className="home-premium-cta-primary rounded-full px-6 py-2.5 text-sm font-semibold"
            >
              Got it
            </button>
          </div>
        </div>
      </PremiumActivityModalShell>

      {showAuthOptions && (
        <PremiumActivityModalShell
          open={showAuthOptions}
          onClose={() => setShowAuthOptions(false)}
          title="Join Artmosphere"
          eyebrow="Welcome"
          subtitle="Start your creative journey"
          maxWidthClass="max-w-md"
          zIndexClass="z-[200100]"
        >
          <div className="space-y-3 p-6">
            <button
              type="button"
              onClick={() => handleAuthChoice('signup')}
              className="home-premium-cta-primary w-full rounded-full py-3 text-sm font-semibold"
            >
              Start Your Artist Journey
            </button>
            <button
              type="button"
              onClick={() => handleAuthChoice('login')}
              className="home-premium-cta-secondary w-full rounded-full py-3 text-sm font-semibold"
            >
              Welcome Back
            </button>
          </div>
        </PremiumActivityModalShell>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onModeSwitch={setAuthMode}
        redirectSignupToFullPage
      />
    </div>
  );
}
